import { useTranslation } from "@/hooks/useTranslation";
import { sourceLink } from "@/lib/source-link";
import type { ReadPagesToolOutput } from "@shaarp/common";
import type { ToolUIPart } from "ai";
import { LinkIcon } from "lucide-react";

/**
 * A `readPages` call inside the research block: what the model is reading
 * while it runs, then the pages it read. The persisted output keeps each page
 * with its content emptied, so the list survives a reload.
 */
export function ChatResearchReadPages({
  part,
  isRunning,
}: {
  part: ToolUIPart;
  isRunning: boolean;
}) {
  const { t } = useTranslation();
  const output = part.output as Partial<ReadPagesToolOutput> | undefined;
  const pages = output?.pages ?? [];
  // A call over with no page is over: "reading…" would promise a read that
  // is not coming.
  const status = isRunning
    ? t("chat.message.research.readingPages")
    : pages.length === 0
      ? t("chat.message.research.noPageRead")
      : t("chat.message.research.pagesRead", { count: pages.length });

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <LinkIcon className="size-3 shrink-0" />
        <span className="truncate">
          {status}
        </span>
      </div>
      {pages.map((page) => {
        const link = sourceLink(page.url);
        return link ? (
          <a
            key={page.url}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <span className="truncate underline-offset-2 hover:underline">
              {page.title || link.host}
            </span>
            <span className="shrink-0 text-muted-foreground/60">
              {link.host}
            </span>
          </a>
        ) : (
          <div key={page.url} className="truncate text-xs text-muted-foreground">
            {page.title || page.url}
          </div>
        );
      })}
    </div>
  );
}
